import { cloneDeep, findIndex } from "lodash";
import { observable } from "mobx";
import { create, persist } from "mobx-persist";
import { HrRoutes } from "../components/hr/router/HrRoutes";
import { debug } from "../components/util/debug";
import { templateToString, transitionToTemplate } from "../components/util/helpers";
import { THEME } from "../theme/ThemeStore";

import {
    abbreviatedLanguageOptions,
    deviceOptions,
    discoveryOptions,
    evidenceDateRangeOptions,
    germanLvlOptions,
    Option,
    rejectedDateRangeOptions
} from "../constants";
import { t } from "../i18n/util";
import { API } from "../network/API";
import { ApplicationStateEnum, Note } from "../network/APITypes";
import {
    DateRangeEnum,
    DiscoveryEnum,
    IGetApplicantDetailsResponse,
    IPostApplicationStateTransitionPayload
} from "../types/models";
import { generalStore } from "./GeneralStore";
import { notify } from "../components/util/notifications";

export const EMPTY_DIALOG_FORM: IPostApplicationStateTransitionPayload = {
    language: abbreviatedLanguageOptions[0].value,
    template: transitionToTemplate(),
    message: templateToString(abbreviatedLanguageOptions[0].value, transitionToTemplate(), {
        firstName: "",
        userName: ""
    }),
    noMessage: false,
    title: "",
    interviewDate1: null,
    interviewStartTime1: null,
    interviewEndTime1: null,
    interviewDate2: null,
    interviewStartTime2: null,
    interviewEndTime2: null,
    interviewDate3: null,
    interviewStartTime3: null,
    interviewEndTime3: null,
    guests: [],
    onlineMeeting: true,
    contract: undefined,
    evidence: "false",
    date: null,
    scheduledEmailDate: null
};

export const EMPTY_APPLICANT: IGetApplicantDetailsResponse = {
    applicationDate: new Date(),
    discovery: discoveryOptions[0].value,
    email: "",
    firstName: "",
    gdprAccepted: true,
    jobCategory: "",
    jobCategoryId: "",
    lastName: "",
    resume: {
        encrypted: false
    },
    salary: "",
    startDate: new Date(),
    workHours: "",
    previousProjects: "",
    phone: "",
    personnelNumber: "",
    experience: 0,
    noticePeriod: "",
    evidence: false,
    germanLvl: THEME.THEME_NAME != "dal" ? germanLvlOptions[3].value : germanLvlOptions[0].value,
    device: deviceOptions[0].value,
    deviceSN: "",
    city: ""
};

export const EMPTY_NOTE: Note = {
    title: "",
    content: "",
    id: "",
    createdAt: new Date(),
    createdBy: {},
    encrypted: false
};

class HrStore {
    @observable isRehydrated = false;
    @observable currentDialogForm: IPostApplicationStateTransitionPayload;

    @observable backTarget: string = HrRoutes.DASHBOARD;
    @observable dashboardViewType: "kanban" | "list";

    @observable applicantHasChanged = true;
    @observable currentApplicantID = "";
    @observable currentApplicant: IGetApplicantDetailsResponse;

    @observable unreadCount = 0;
    @observable lastRead = new Date();
    @observable forceReloadApplicationState?: ApplicationStateEnum;

    // filters
    @persist("list") @observable selectedJobCategoryIds: string[] = [];
    @observable selectedApplicationStates: string[] = [];
    @observable evidenceLastChangeSince: Option<DateRangeEnum> = evidenceDateRangeOptions[1];
    @observable rejectedLastChangeSince: Option<DateRangeEnum> = rejectedDateRangeOptions[1];

    constructor() {
        this.currentDialogForm = cloneDeep(EMPTY_DIALOG_FORM);
        this.currentApplicant = cloneDeep(EMPTY_APPLICANT);
        this.dashboardViewType = "kanban";
    }

    loadApplicant = async (applicantId?: string) => {
        if (!applicantId && !this.currentApplicant) {
            return;
        }

        try {
            generalStore.isLoading = true;
            if (applicantId) {
                this.currentApplicantID = applicantId;
            }
            const response = await API.getApplicant(this.currentApplicantID);
            if (response) {
                const tmpWorkHours: number = cloneDeep(response.workHours);
                let tmpDiscovery: DiscoveryEnum;
                let tmpCustomDiscoverySource = "";

                if (
                    findIndex(discoveryOptions, function (o) {
                        return o.value === response.discovery;
                    }) >= 0
                ) {
                    tmpDiscovery = response.discovery as DiscoveryEnum;
                } else {
                    tmpDiscovery = "different_source";
                    tmpCustomDiscoverySource = response.discovery;
                }

                this.currentApplicant = {
                    ...response,
                    workHours: tmpWorkHours.toString(),
                    discovery: tmpDiscovery,
                    customDiscoverySource: tmpCustomDiscoverySource
                } as IGetApplicantDetailsResponse;
            }
        } catch (err) {
            generalStore.setError(t("error.loadApplication"), err);
        } finally {
            generalStore.isLoading = false;
        }
    };

    loadNotifications = async () => {
        try {
            const { unreadNotifications, lastRead } = await API.getUnreadNotificationCount();

            const changed = this.unreadCount !== unreadNotifications;
            this.unreadCount = unreadNotifications;
            this.lastRead = lastRead;

            if (this.unreadCount > 0 && changed) {
                notify(
                    t("notifications.title"),
                    this.unreadCount === 1
                        ? t("notifications.message.singular")
                        : t("notifications.message", { notifications: this.unreadCount })
                );
            }
        } catch (error) {
            generalStore.setError(t("error.loadNotifications"), error);
        }
    };
}

const hydrate = create({
    storage: require("localforage")
});

const hrStore = new HrStore();

hydrate("hr", hrStore)
    .then(() => {
        hrStore.isRehydrated = true;
        debug.log("rehydrated hrStore");
    })
    .catch((error) => {
        console.error(error);
    });

export { hrStore };
